import { BigNumberCard, BreakPointCondition, useBreakpointIf, HStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { Box, ScrollView } from 'native-base';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import {
  fundraisingAucQuery,
  fundraisingConsolidatedQuery,
  getHomeValueVisibility,
} from '../../../../../../store/home/capture/selector';
import {
  FundraisingAucResponseNormalized,
  FundraisingConsolidatedSuccessResponseNormalized,
} from '../../../../../../store/home/capture/types';
import { BigNumberCardContainer, BigNumberCardContainerTypes } from '../BigNumberCardContainer';

interface CaptureBigNumbersProps {
  isMobile?: boolean;
}

export const CaptureBigNumbers: React.FC<CaptureBigNumbersProps> = ({ isMobile }) => {
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');
  const valueVisible = useRecoilValue(getHomeValueVisibility);
  const [dataConsolidatedAuc, setDataConsolidatedAuc] =
    useState<FundraisingAucResponseNormalized>();
  const [dataConsolidated, setDataConsolidated] =
    useState<FundraisingConsolidatedSuccessResponseNormalized>();
  const periodOptions: BigNumberCardContainerTypes.PeriodOption[] = [
    { label: 'YTD', value: 'ytd' },
  ];

  const {
    data: consolidatedAuc,
    loading: consolidatedAucIsLoading,
    error: consolidatedAucHasError,
    refresher: consolidatedAucRefresh,
  } = useRecoilQuery(fundraisingAucQuery);

  const {
    data: consolidated,
    loading: consolidatedIsLoading,
    error: consolidatedHasError,
    refresher: consolidatedRefresh,
  } = useRecoilQuery(fundraisingConsolidatedQuery);

  useEffect(() => {
    const items = consolidatedAuc?.data;

    if (!consolidatedAucIsLoading && items) {
      setDataConsolidatedAuc(items);
    }
  }, [consolidatedAucIsLoading, consolidatedAuc]);

  useEffect(() => {
    const items = consolidated?.data;

    if (!consolidatedIsLoading && items) {
      setDataConsolidated(items);
    }
  }, [consolidatedIsLoading, consolidated]);

  const currentPeriod: BigNumberCardContainerTypes.PeriodOption = {
    label: 'ATUAL',
    value: 'current',
  };

  const leftContent = useMemo(
    () => (
      <BigNumberCard
        isLoading={consolidatedAucIsLoading}
        iconName="Profit"
        label="AUC"
        onReload={consolidatedAucRefresh}
        hasError={!!consolidatedAucHasError}
        hasShadow={false}
        value={dataConsolidatedAuc?.totalValueNormalized}
        flex={1}
        isVisible={valueVisible}
      />
    ),
    [
      consolidatedAucIsLoading,
      consolidatedAucRefresh,
      consolidatedAucHasError,
      dataConsolidatedAuc?.totalValueNormalized,
      valueVisible,
    ]
  );

  const rightContent = useMemo(
    () => (
      <>
        <BigNumberCard
          isLoading={consolidatedIsLoading}
          iconName="Capture"
          label="APORTE"
          value={dataConsolidated?.grossFundraisingValue}
          flex={1}
          onReload={consolidatedRefresh}
          isVisible={valueVisible}
          hasError={!!consolidatedHasError}
          hasShadow={!isMobile}
        />
        <BigNumberCard
          isLoading={consolidatedIsLoading}
          iconName="Reclaim"
          label="RESGATE"
          value={dataConsolidated?.withdrawValue}
          flex={1}
          onReload={consolidatedRefresh}
          hasError={!!consolidatedHasError}
          hasShadow={!isMobile}
          isVisible={valueVisible}
        />
        <BigNumberCard
          isLoading={consolidatedIsLoading}
          iconName="Receipt"
          label="CAPTAÇÃO LÍQUIDA"
          value={dataConsolidated?.netFundraisingValue}
          flex={1}
          onReload={consolidatedRefresh}
          hasError={!!consolidatedHasError}
          hasShadow={!isMobile}
          isVisible={valueVisible}
        />
      </>
    ),
    [
      consolidatedIsLoading,
      dataConsolidated?.netFundraisingValue,
      dataConsolidated?.withdrawValue,
      dataConsolidated?.grossFundraisingValue,
      consolidatedRefresh,
      valueVisible,
      consolidatedHasError,
      isMobile,
    ]
  );

  const content = (
    <Box paddingX={Spacing['stack.xxxs']} paddingY={{ base: Spacing['stack.xxxs'] }}>
      <HStack space={Spacing['stack.xxxs']} minWidth="max-content" flex={1}>
        <BigNumberCardContainer
          periodOptions={currentPeriod}
          selectedPeriod={currentPeriod.value}
          periodText={dataConsolidatedAuc?.currentDateNormalized}
          isLoading={consolidatedAucIsLoading}
          width={{ base: '270px', sm: '2/6' }}>
          {leftContent}
        </BigNumberCardContainer>
        <BigNumberCardContainer
          periodOptions={periodOptions}
          selectedPeriod={periodOptions[0].value}
          periodText={`${dataConsolidated?.startDateNormalized} até ${dataConsolidated?.endDateNormalized}`}
          isLoading={consolidatedIsLoading}
          flex={1}
          minWidth="max-content">
          {rightContent}
        </BigNumberCardContainer>
      </HStack>
    </Box>
  );

  if (isMobile || isTablet) {
    return (
      <ScrollView horizontal persistentScrollbar={false} showsHorizontalScrollIndicator={false}>
        {content}
      </ScrollView>
    );
  } else {
    return content;
  }
};
